import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { Log } from '@/shared/Log';
import { PfaPhysiotherapyAppProps } from '@/features/pfaPhysiotherapy/app/PfaPhysiotherapyApp';

export enum PfaPhysioStep {
    Landing = 0,
    SelectPatient = 1,
    Consent = 2,
}

export enum PfaQueryKeys {
    Page = 'page',
    PatientUid = 'patientUid',
}

export type PfaUrlState = Partial<Record<PfaQueryKeys, string | null>>;

type PfaPhysioFlowStep = {
    page: PfaPhysioStep;
    isEnabled: (state: PfaUrlState) => boolean;
};

const QUESTIONNAIRE_ENTRY_URL =
    '/pfaphysiotherapy/questionnaire/where-does-it-hurt';

const parsePage = (page: string | null | undefined): PfaPhysioStep | null => {
    if (!page) return null;
    return PfaPhysioStep[page as keyof typeof PfaPhysioStep] ?? null;
};

export const usePfaPhysiotherapyFlow = ({
    appProps,
}: {
    appProps: PfaPhysiotherapyAppProps;
}) => {
    const { showPatientSelection, parentUid } = appProps;
    const [searchParams, setSearchParams] = useSearchParams();

    const urlState: PfaUrlState = useMemo(
        () =>
            Object.fromEntries(
                Object.values(PfaQueryKeys).map((key) => [
                    key,
                    searchParams.get(key),
                ]),
            ),
        [searchParams],
    );

    const steps: PfaPhysioFlowStep[] = useMemo(
        () => [
            {
                page: PfaPhysioStep.Landing,
                isEnabled: () => true,
            },
            {
                page: PfaPhysioStep.SelectPatient,
                isEnabled: () => showPatientSelection,
            },
            {
                page: PfaPhysioStep.Consent,
                isEnabled: () => true,
            },
        ],
        [showPatientSelection],
    );

    const isStepValid = (page: PfaPhysioStep, state: PfaUrlState): boolean => {
        switch (page) {
            case PfaPhysioStep.Landing:
                return true;
            case PfaPhysioStep.SelectPatient:
            case PfaPhysioStep.Consent:
                return !!state.patientUid;
            default:
                return false;
        }
    };

    const updateUrl = (state: PfaUrlState, replace: boolean) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(state).forEach(([key, value]) => {
            if (value == null) {
                next.delete(key);
            } else {
                next.set(key, value);
            }
        });
        setSearchParams(next, { replace });
    };

    const currentPage = parsePage(urlState.page);

    // Reset to the first step if the URL points to an unknown/disabled step,
    // or if a previous step's state is missing (e.g. a manually edited URL)
    useEffect(() => {
        const currentIndex = steps.findIndex((s) => s.page === currentPage);
        const currentStep = steps[currentIndex];
        const hasMissingPreviousState = steps
            .slice(0, currentIndex)
            .some(
                (s) => s.isEnabled(urlState) && !isStepValid(s.page, urlState),
            );
        const isMissingPatient =
            currentPage === PfaPhysioStep.Consent && !urlState.patientUid;

        if (
            currentStep == null ||
            !currentStep.isEnabled(urlState) ||
            hasMissingPreviousState ||
            isMissingPatient
        ) {
            updateUrl(
                {
                    [PfaQueryKeys.Page]: PfaPhysioStep[PfaPhysioStep.Landing],
                    [PfaQueryKeys.PatientUid]: null,
                },
                true,
            );
        }
    }, [currentPage]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage]);

    const navigateBack = () => {
        window.history.back();
    };

    const navigateForward = (updatedUrlState: PfaUrlState | null = null) => {
        const state = updatedUrlState ?? urlState;

        const currentIndex = steps.findIndex(
            (s) => s.page === parsePage(state.page),
        );
        const currentStep = steps[currentIndex];

        if (currentStep == null || !isStepValid(currentStep.page, state)) {
            return;
        }

        const nextStep = steps
            .slice(currentIndex + 1)
            .find((s) => s.isEnabled(state));

        if (nextStep === undefined) {
            Log.error(
                `Next step was undefined in PFA physiotherapy navigateForward().
                Navigating from: ${PfaPhysioStep[currentStep.page]}`,
                { ...state },
            );
            return;
        }

        updateUrl(
            {
                ...state,
                [PfaQueryKeys.Page]: PfaPhysioStep[nextStep.page],
            },
            false,
        );
    };

    const onLandingNext = () => {
        // When patient selection is skipped, the parent is always the patient
        const state: PfaUrlState = showPatientSelection
            ? urlState
            : { ...urlState, [PfaQueryKeys.PatientUid]: parentUid };
        navigateForward(state);
    };

    const onSelectPatient = (patientUid: string) => {
        navigateForward({ ...urlState, [PfaQueryKeys.PatientUid]: patientUid });
    };

    const onAcceptConsent = () => {
        if (!urlState.patientUid) return;

        window.location.href = `${QUESTIONNAIRE_ENTRY_URL}?selectedPatientUid=${encodeURIComponent(urlState.patientUid)}`;
    };

    return {
        currentPage,
        selectedPatientUid: urlState.patientUid ?? null,
        navigateBack,
        onLandingNext,
        onSelectPatient,
        onAcceptConsent,
    };
};
